/**
 * PendingInvitationCard
 * Shared card for pending invitations (friends or groups) with Yes/No (received) or remove (sent)
 * Used by FriendCardPending and GROUP_PENDING in FriendsList
 */

import React, { useMemo } from 'react';
import {
  View,
  Text,
  Pressable,
  Platform,
  ActivityIndicator,
  GestureResponderEvent,
} from 'react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { Check, X, Trash2, Clock } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { getBaseInvitationStyles } from '../BaseInvitation/BaseInvitation.styles';

export type PendingInvitationVariant = 'received' | 'sent';

interface PendingInvitationCardReceivedProps {
  variant: 'received';
  title: string;
  message: string;
  onAccept: () => void;
  onDecline: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  errorMessage?: string | null;
  testID?: string;
}

interface PendingInvitationCardSentProps {
  variant: 'sent';
  primaryName: string;
  statusText: string;
  secondaryText?: string | null;
  onRemove?: (e: GestureResponderEvent) => void;
  testID?: string;
}

export type PendingInvitationCardProps =
  | PendingInvitationCardReceivedProps
  | PendingInvitationCardSentProps;

export const PendingInvitationCard: React.FC<PendingInvitationCardProps> = (props) => {
  const theme = useTheme();
  const styles = useMemo(() => getBaseInvitationStyles(theme), [theme]);

  if (props.variant === 'received') {
    const {
      title,
      message,
      onAccept,
      onDecline,
      isLoading = false,
      disabled = false,
      errorMessage,
      testID,
    } = props;

    if (errorMessage) {
      return (
        <View style={styles.receivedInvitationContainer}>
          <Text style={styles.errorText}>{errorMessage}</Text>
        </View>
      );
    }

    return (
      <View style={styles.receivedInvitationContainer} testID={testID}>
        <Text style={styles.inviterNameText}>{title}</Text>
        <Text style={styles.invitationMessage}>{message}</Text>
        <View style={styles.actionRow}>
          <Pressable
            style={({ pressed, hovered }) => [
              styles.acceptButton,
              (isLoading || disabled) && styles.buttonDisabled,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              !(isLoading || disabled) && pressed && styles.acceptButtonPressed,
              !(isLoading || disabled) && hovered && Platform.OS === 'web' && styles.acceptButtonHover,
            ]}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            onPress={onAccept}
            disabled={isLoading || disabled}
            testID={testID ? `${testID}-accept-button` : undefined}
          >
            {isLoading ? (
              <ActivityIndicator size="small" color={theme.white} />
            ) : (
              <>
                <Check size={16} color={theme.white} style={styles.buttonIcon} />
                <Text style={styles.acceptButtonText}>Yes</Text>
              </>
            )}
          </Pressable>
          <Pressable
            style={({ pressed, hovered }) => [
              styles.declineButton,
              (isLoading || disabled) && styles.buttonDisabled,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              !(isLoading || disabled) && pressed && styles.declineButtonPressed,
              !(isLoading || disabled) && hovered && Platform.OS === 'web' && styles.declineButtonHover,
            ]}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            onPress={onDecline}
            disabled={isLoading || disabled}
            testID={testID ? `${testID}-decline-button` : undefined}
          >
            <X size={16} color={theme.gray[700]} style={styles.buttonIcon} />
            <Text style={styles.declineButtonText}>No</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  // variant === 'sent'
  const { primaryName, statusText, secondaryText, onRemove, testID } = props;

  return (
    <View style={styles.sentInvitationContainer} testID={testID}>
      {onRemove != null ? (
        <Pressable
          onPress={onRemove}
          style={({ pressed, hovered }) => [
            styles.pendingRemoveButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.pendingRemoveButtonPressed,
            hovered && Platform.OS === 'web' && styles.pendingRemoveButtonHover,
          ]}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          testID={testID ? `${testID}-remove-button` : undefined}
        >
          <Trash2 size={20} color={theme.error} />
        </Pressable>
      ) : null}
      <View style={styles.pendingContent}>
        <Clock size={24} color={theme.gray[600]} style={styles.waitingIcon} />
        <Text style={styles.waitingText} numberOfLines={1}>
          {primaryName}
        </Text>
        <Text style={styles.pendingStatusText}>{statusText}</Text>
        {secondaryText ? (
          <Text style={styles.invitedEmailText} numberOfLines={1}>
            {secondaryText}
          </Text>
        ) : null}
      </View>
    </View>
  );
};
